import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingScreen } from '../shared/LoadingScreen';
import { useTranslation } from 'react-i18next';

import { logger } from '@deskbay/shared';
export function RootRedirect() {
    const { session, restaurantData, hasInitialized, authResolved } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // =========================================================================
    // CRITICAL: Handle OAuth error hashes FIRST to prevent infinite loop
    // When Supabase returns #error_code=... in the URL, we must intercept it
    // immediately, clear the hash, and redirect to login with an error message.
    // Without this, hasSupabaseHash stays true forever → hasInitialized never true → loop
    // =========================================================================
    const hasOAuthError = location.hash.includes('error_code') || location.hash.includes('error_description');

    useEffect(() => {
        if (hasOAuthError) {
            // Parse the error from the hash
            const hashParams = new URLSearchParams(location.hash.replace('#', ''));
            const errorDescription = hashParams.get('error_description') || 'Errore durante il login';
            const errorCode = hashParams.get('error_code') || 'unknown';

            logger.auth.error('OAuth error detected:', errorCode, 'RootRedirect', {
                errorDescription,
                fullHash: location.hash,
                href: window.location.href,
            });

            // Clear the hash to break the loop
            window.history.replaceState(null, '', window.location.pathname);

            // Redirect to login with error state
            navigate('/login', {
                replace: true,
                state: {
                    oauthError: true,
                    errorMessage: decodeURIComponent(errorDescription.replace(/\+/g, ' '))
                }
            });
        }
    }, [hasOAuthError, location.hash, navigate]);


    // If we have an OAuth error, show loading while the redirect happens
    if (hasOAuthError) {
        return <LoadingScreen />;
    }

    // CRITICO: Proteggi hash Supabase da redirect prematuro
    // Only access_token/refresh_token hashes need waiting (NOT error_code — handled above)
    const hasSupabaseHash =
        location.hash.includes('access_token') ||
        location.hash.includes('refresh_token');

    // Block routing while:
    // 1. An OAuth hash is being processed (Supabase JS consuming the token)
    // 2. Auth has not yet initialized (first INITIAL_SESSION + loadAppData not complete)
    // 3. Il listener auth non si è ancora pronunciato: il safety timer di AuthContext
    //    può forzare hasInitialized=true prima che la sessione sia nota, e i rami qui
    //    sotto interpreterebbero `session === null` come "non loggato" mandando su
    //    /welcome un utente con sessione valida. `authResolved` ha un timeout proprio
    //    (12s), quindi questa condizione non può bloccare il routing per sempre.
    // Without this, the component falls through to /welcome before the session is ready,
    // causing a visible flash of WelcomePage for logged-in users returning from OAuth.
    if (hasSupabaseHash || !hasInitialized || !authResolved) {
        return <LoadingScreen />;
    }

    // No session after auth initialized → Welcome page
    if (!session && !restaurantData) {
        return <Navigate to="/welcome" replace state={{ fromRoot: true }} />;
    }

    // --- A questo punto i dati sono caricati ---

    // Detect OAuth provider from session (Google or Apple)
    const oauthProvider = session?.user?.app_metadata?.provider;
    const isOAuthUser = oauthProvider === 'google' || oauthProvider === 'apple';

    // Loggato con ristorante → controlla se onboarding è completato
    if (session && restaurantData) {
        // Se l'onboarding non è completato, manda a /onboarding per finire
        // Questo copre tutti gli step incluso il finale (status 'finalizing')
        if (restaurantData.onboarding_status && restaurantData.onboarding_status !== 'completed') {
            // For OAuth users, add provider param so isSocialRedirect triggers in OnboardingWizard
            const providerParam = isOAuthUser ? `&provider=${oauthProvider}` : '';
            const onboardingUrl = `/onboarding?restaurantId=${restaurantData.directoryId}${providerParam}`;
            return <Navigate to={onboardingUrl} replace />;
        }
        // Onboarding completato → dashboard
        // If user came from a social signup attempt but already has an account,
        // show an informational message so they understand why they landed on the dashboard.
        // Check for pending post-login redirect (e.g., from iOS "Apri Account Web" → Safari login)
        const pendingRedirect = (() => {
            try {
                const r = sessionStorage.getItem('deskbay_post_login_redirect');
                if (r) sessionStorage.removeItem('deskbay_post_login_redirect');
                return r;
            } catch { return null; }
        })();
        const redirectTarget = (pendingRedirect && pendingRedirect.startsWith('/dashboard'))
            ? pendingRedirect : '/dashboard';

        const fromSignup = location.search.includes('provider=') || hasSupabaseHash;
        if (isOAuthUser && fromSignup) {
            const providerName = oauthProvider === 'apple' ? 'Apple' : 'Google';
            return <Navigate to={redirectTarget} replace state={{
                infoMessage: t('auth.social_already_registered', { provider: providerName })
            }} />;
        }
        return <Navigate to={redirectTarget} replace />;
    }

    // Loggato senza ristorante → onboarding
    // For OAuth users (Google/Apple), add provider param so OnboardingWizard skips to SearchStep
    if (session && !restaurantData) {
        const providerParam = isOAuthUser ? `?provider=${oauthProvider}` : '';
        return <Navigate to={`/onboarding${providerParam}`} replace />;
    }

    // Non loggato → Welcome page
    return <Navigate to="/welcome" replace />;
}
